import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { routeTree } from "../routeTree.gen";
import { setupStore } from "../state/store";
import { Provider as ReduxProvider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import * as Sentry from "@sentry/react";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/ui/toaster";
import { useAuth } from "@/auth/hooks/useAuth";
import { queryClient } from "@/api";

Sentry.init({
  dsn: "https://653e2bb77b8faacfe92d062594a3bde1@o366744.ingest.us.sentry.io/4509757005824000",
  sendDefaultPii: true,
  environment: import.meta.env.VITE_ENVIRONMENT,
  enabled: import.meta.env.VITE_ENVIRONMENT === "production",
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    isAuthenticated: undefined!,
    isStaff: false,
  },
});

const store = setupStore();
const persistor = persistStore(store);

export const App: React.FC = () => (
  <StrictMode>
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} position="left" buttonPosition="top-right" />
          <Toaster />
          <Router />
        </QueryClientProvider>
      </PersistGate>
    </ReduxProvider>
  </StrictMode>
);

const Router: React.FC = () => {
  const { isAuthenticated, isStaff } = useAuth();
  return <RouterProvider router={router} context={{ isAuthenticated, isStaff }} />;
};
